import React, { useState, useEffect } from 'react';
import HolsterVisualizer from '../HolsterVisualizer';
import ColorSelector from '../ColorSelector';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';
import DisclaimerBox from '../DisclaimerBox'; 
import CollapsibleSection from '../CollapsibleSection';

// Global Settings
import { 
  ENABLE_RUSH_SHIPPING, 
  RUSH_PRICE, 
  GLOBAL_DISCOUNT_PERCENT, 
  SALE_NAME,
  RUSH_DAYS, 
  CUSTOM_DAYS,  
  RUSH_TIMELINE_TEXT, 
  CUSTOM_TIMELINE_TEXT,
  // Lead time labels + colors
  CUSTOM_LABEL, CUSTOM_COLOR, RUSH_COLOR, leadTimeTint,
  optionLine,
} from '../config/globalSettings';

// --- IMPORT MASTER BLASTER DATA ---
import { holsterData } from '../data/masterBlasterData'; 
import '../App.css';

function MasterBlasterBuilder() {
  // --- STATE ---
  const [viewSide, setViewSide] = useState("front"); 
  const [handId, setHandId] = useState("right");
  const [brandId, setBrandId] = useState(""); 
  const [gunId, setGunId] = useState("");
  const [colorId, setColorId] = useState("");
  const [mountId, setMountId] = useState("");
  const [clawId, setClawId] = useState(""); 
  const [breakthroughId, setBreakthroughId] = useState("");
  
  // Rush Service
  const [rushService, setRushService] = useState(false);

  // --- SHARE FUNCTIONALITY ---
  const {
    getConfigFromURL,
    generateShareableURL,
    saveToLocalStorage,
    loadFromLocalStorage,
    savedConfigs,
    saveNamedConfig,
    deleteNamedConfig,
    copyToClipboard,
    copySuccess,
    shareModalOpen,
    setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    handId, brandId, gunId, colorId, mountId, clawId, 
    breakthroughId, rushService
  });

  const loadConfig = (config) => {
    if (!config) return;
    setHandId(config.handId || 'right');
    setBrandId(config.brandId || '');
    setGunId(config.gunId || '');
    setColorId(config.colorId || '');
    setMountId(config.mountId || '');
    setClawId(config.clawId || '');
    setBreakthroughId(config.breakthroughId || '');
    setRushService(config.rushService || false);
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []);

  useEffect(() => {
    if (gunId) saveToLocalStorage(getCurrentConfig());
  }, [handId, brandId, gunId, colorId, mountId, clawId, breakthroughId, rushService]);

  const shareableURL = generateShareableURL(getCurrentConfig());

  // --- FILTERING ---
  const availableGuns = brandId 
    ? holsterData.guns.filter(g => g.brand_id === brandId)
    : [];

  // --- HELPERS ---
  const selectedGun = holsterData.guns.find(g => g.id === gunId);
  const selectedColor = holsterData.colors.find(c => c.id === colorId);
  const selectedMount = holsterData.mounts.find(m => m.id === mountId);
  const selectedClaw = holsterData.claws.find(c => c.id === clawId);
  const selectedBreakthrough = holsterData.breakthrough.find(b => b.id === breakthroughId);

  // --- PRICING ---
  const BASE_PRICE = 66.99;

  // 1. Calculate Base Subtotal
  const subtotal = 
    BASE_PRICE +
    (selectedColor?.price || 0) +
    (selectedMount?.price || 0) +
    (selectedClaw?.price || 0) +
    (selectedBreakthrough?.price || 0) +
    (rushService && ENABLE_RUSH_SHIPPING ? RUSH_PRICE : 0);

  // 2. Apply Global Sale Discount
  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;

  // 3. Final Total
  const total = (subtotal - discountAmount).toFixed(2);

  // --- LEAD TIME (PULLED FROM GLOBAL SETTINGS) ---
  const calculateLeadTime = () => {
    if (!selectedGun) return { type: "PENDING", text: "Select Options" };
    
    const targetDays = rushService ? RUSH_DAYS : CUSTOM_DAYS;
    
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    
    if (rushService && ENABLE_RUSH_SHIPPING) {
        return {
            type: "RUSH PRIORITY",
            timeline: RUSH_TIMELINE_TEXT,
            date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            color: RUSH_COLOR,
            bg: leadTimeTint(RUSH_COLOR),
            isCustom: true
        };
    }

    return {
      type: CUSTOM_LABEL,
      timeline: CUSTOM_TIMELINE_TEXT,
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      color: CUSTOM_COLOR,
      bg: leadTimeTint(CUSTOM_COLOR),
      isCustom: true 
    };
  };

  const leadTimeInfo = calculateLeadTime();
  
  const isReady = brandId && gunId && colorId && mountId && clawId && breakthroughId;

  // --- ADD TO CART FUNCTION ---
  const handleAddToCart = () => {
    if (!isReady) return;

    const configuration = [
        `Gun Model: ${selectedGun?.name}`,
        `Hand: ${handId === 'right' ? 'Right' : 'Left'}`,
        `Color: ${selectedColor?.name}`,
        `Mount: ${selectedMount?.name}`,
        optionLine("Claw", selectedClaw?.name),
        optionLine("Breakthrough Clean", selectedBreakthrough?.name),
        `Warranty: Free Lifetime Warranty`,
        `Lead Time: ${leadTimeInfo.timeline}`, 
    ].filter(Boolean).join('\n');

    const productID = 283909; // LIVE MASTER BLASTER PRODUCT ID
    
    const params = new URLSearchParams();
    params.append('add-to-cart', productID);
    params.append('quantity', '1');
    params.append('anr_config', configuration);
    params.append('anr_price', total);

    const addToCartURL = `/checkout/?${params.toString()}`;
    window.location.href = addToCartURL;
  };

  return (
    <div className={`builder-container ${colorId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">The Master Blaster™<br/><span style={{fontSize:"16px", color:"#D5A678"}}>MAG & PISTOL COMBO</span></h1>
        
        {/* FEATURES */}
        <div style={{ color: "#aaa", fontSize: "14px", lineHeight: "1.7", marginBottom: "40px" }}>
           <h3 style={{ border: "none", padding: 0, color: "#fff", textTransform: "uppercase" }}>Features</h3>
           <ul style={{ paddingLeft: "20px", marginBottom: "20px", listStyleType: "disc" }}>
             <li><strong style={{ color: "#fff" }}>Minimalist Sidecar:</strong> Angled off-shoot carries a backup mag.</li>
             <li><strong style={{ color: "#fff" }}>Subcompact Optimized:</strong> Designed specifically for smaller carry guns.</li>
             <li><strong style={{ color: "#fff" }}>Monoblock Clip:</strong> Secure retention without bulk.</li>
             <li><strong style={{ color: "#fff" }}>Full Trigger Guard:</strong> Complete protection for AIWB carry.</li>
             <li><strong style={{ color: "#fff" }}>Adjustable Retention:</strong> Dial in your perfect draw.</li>
           </ul>
        </div>

        {/* INPUTS */}
        <SegmentedControl 
          label="Dominant Hand" 
          options={holsterData.hands} 
          selectedValue={handId} 
          onChange={(val) => {
              setHandId(val);
              setGunId(""); 
          }} 
        />
        
        <OptionSelector label="Select Manufacturer *" options={holsterData.brands} selectedValue={brandId} onChange={(val) => { setBrandId(val); setGunId(""); }} />
        <OptionSelector label="Subcompact Model *" options={availableGuns} selectedValue={gunId} onChange={setGunId} disabled={!brandId} />

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
        
        {/* COLORS WITH TOOLTIP */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
          <h3 style={{ textTransform: "uppercase", margin: 0 }}>Color Options</h3>
          <div className="tooltip-wrapper">
             <span className="info-icon">i</span>
             <div className="tooltip-content">
                ANR Design does not have control over the pattern. The model portrayed in the configurator is a representation.
             </div>
          </div>
        </div>
        <ColorSelector options={holsterData.colors} selectedValue={colorId} onChange={setColorId} />
        
        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* MOUNTING WITH MEGA TOOLTIP */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
          <h3 style={{ textTransform: "uppercase", margin: 0 }}>Mounting Options</h3>
          <div className="tooltip-wrapper">
              <span className="info-icon">i</span>
              <div className="tooltip-content mega-tooltip">
                  {holsterData.mounts.map(mount => (
                      <div key={mount.id} className="mount-info-row" style={{marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #555'}}>
                          <div className="mount-text">
                              <h4 style={{marginTop: 0, marginBottom: '5px'}}>{mount.name}</h4>
                              <p style={{margin: 0}}>{mount.description}</p>
                          </div>
                      </div>
                  ))}
              </div>
          </div>
        </div>
        <OptionSelector label="Select Clip *" options={holsterData.mounts} selectedValue={mountId} onChange={setMountId} />

        {/* CLAW WITH TOOLTIP */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "30px", marginBottom: "10px" }}>
           <h3 style={{ textTransform: "uppercase", margin: 0 }}>Concealment Claw</h3>
           <div className="tooltip-wrapper">
              <span className="info-icon">i</span>
              <div className="tooltip-content">
                  Injection molded accessory designed to reduce printing while concealed carrying. The claw tucks behind your pant seam which helps keep the grip of the pistol from protruding.
              </div>
           </div>
        </div>
        <OptionSelector label="Select Claw Option" options={holsterData.claws} selectedValue={clawId} onChange={setClawId} />

        {/* --- FREE LIFETIME WARRANTY BANNER --- */}
        <div style={{ marginTop: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <h3 style={{ textTransform: "uppercase", margin: 0 }}>Lifetime Warranty</h3>
                <div className="tooltip-wrapper">
                    <span className="info-icon">i</span>
                    <div className="tooltip-content">
                        Every ANR Design holster is backed by an automatic Lifetime Warranty on the Kydex® shell for the original purchaser. If your shell cracks or breaks under normal, intended use, we replace it. Consumables (hardware, loops, spacers), cosmetic wear, and damage from modifications or extreme heat are not covered. Return shipping fees apply.
                        <br/><br/>
                        <a href="https://www.anrkydexholsters.com/warranty-privacy-and-shipping-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                            Learn More & File a Claim
                        </a>
                    </div>
                </div>
            </div>
            <div style={{
                padding: "15px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                borderLeft: "4px solid #4caf50",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#4caf50",
                fontWeight: "600"
            }}>
                ✓ Free Lifetime Warranty Included
            </div>
        </div>

        {/* BREAKTHROUGH */}
        <div style={{ marginTop: "30px", marginBottom: "15px" }}>
           <h3 style={{ textTransform: "uppercase", margin: 0 }}>Add Breakthrough Clean</h3>
           <div style={{ 
               backgroundColor: "#1e1e1e", 
               borderLeft: "2px solid #aaa", 
               padding: "10px", 
               marginTop: "10px", 
               fontSize: "13px", 
               color: "#ccc", 
               lineHeight: "1.5" 
           }}>
             <strong>Optional Add-On:</strong> Premium firearm cleaning solvents and lubricants. <br/>
             <em>Not required for the holster itself.</em>
           </div>
        </div>
        <OptionSelector label="Optional firearm maintenance" options={holsterData.breakthrough} selectedValue={breakthroughId} onChange={setBreakthroughId} />

        <hr style={{margin:"50px 0 20px 0", borderTop:"1px solid #333"}}/>

        {/* COLLAPSIBLE DESCRIPTION */}
        <CollapsibleSection title="Description">
            <p style={{ marginTop: 0 }}>
                The <strong>Master Blaster™</strong> is undoubtedly the most minimalist combo Appendix KYDEX® Thermoplastics IWB holster on the market. 
                Typically called a sidecar holster, the Master Blaster™ follows the same style, with a twist. 
                With the standard construction of our normal IWB holsters, the Master Blaster™ has a small angled 
                off-shoot that carries a backup magazine.
            </p>
        </CollapsibleSection>

        {/* COLLAPSIBLE WARNINGS */}
        <div className="mobile-only-content">
            <CollapsibleSection title="Important Notes & Warnings">
                <DisclaimerBox mode="mobile" setShareModalOpen={setShareModalOpen} gunId={gunId} />
            </CollapsibleSection>
        </div>

      </div>

      {/* PREVIEW SECTION */}
      <div className="preview-section">
        <div className="sticky-wrapper">

          <HolsterVisualizer 
            holsterType="masterblaster" 
            gunData={selectedGun} 
            colorData={selectedColor} 
            mountData={selectedMount} 
            clawData={selectedClaw} 
            isLightbearing="no" 
            isLeftHanded={handId === "left"} 
            viewSide={viewSide} 
          />

          {/* --- FRONT / REAR TOGGLE --- */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          {/* --- LEAD TIME BOX --- */}
          {leadTimeInfo.type !== "PENDING" && (
             <div className="lead-time-box" style={{ 
                 backgroundColor: leadTimeInfo.bg, 
                 padding: "15px", 
                 borderLeft: `4px solid ${leadTimeInfo.color}`, 
                 marginBottom: "20px"
             }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                        {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "16px" }}>{leadTimeInfo.type}</strong>}
                        <div style={{ marginTop: "5px", color: "#fff" }}>
                        Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                        Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                        </div>
                    </div>

                    {/* RUSH TOGGLE */}
                    {leadTimeInfo.isCustom && ENABLE_RUSH_SHIPPING && (
                        <div style={{ marginLeft: "15px" }}>
                            <label style={{ 
                                display: "flex", alignItems: "center", cursor: "pointer",
                                background: rushService ? "#D5A678" : "transparent",
                                border: "1px solid #d32f2f", padding: "12px 20px", borderRadius: "4px",
                                transition: "all 0.2s"
                            }}>
                                <input 
                                    type="checkbox" 
                                    checked={rushService} 
                                    onChange={(e) => setRushService(e.target.checked)}
                                    style={{ marginRight: "8px", accentColor: "#000" }} 
                                />
                                <div style={{ lineHeight: "1.7" }}>
                                    <div style={{ fontSize: "15px", fontWeight: "bold", color: rushService ? "#000" : "#D5A678", textTransform: "uppercase" }}>RUSH MY BUILD</div>
                                    <div style={{ fontSize: "15px", color: rushService ? "#000" : "#ccc" }}>+${RUSH_PRICE.toFixed(2)}</div>
                                </div>
                            </label>
                        </div>
                    )}
                </div>
             </div>
          )}
          {leadTimeInfo.type === "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "15px", color: "#666", fontSize: "12px", marginBottom: "15px" }}>Select options to see lead time</div>
          )}
          
          {/* PRICE TAG */}
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>
                    ${subtotal.toFixed(2)}
                </span>
                <span style={{ color: "#00ff00" }}>
                    ${total}
                </span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase", marginTop: "5px" }}>
                    {SALE_NAME} ACTIVE
                </div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>

          <DisclaimerBox mode="desktop" setShareModalOpen={setShareModalOpen} gunId={gunId} />

          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>
            {isReady ? "ADD TO CART" : "SELECT OPTIONS"}
          </button>
        </div>
      </div>
      
      {/* MOBILE BAR */}
      <div className="mobile-action-bar mobile-only-content">
        <div className="mobile-price-container">
          <span className="mobile-label">Estimated Total</span>
          <span className="mobile-total">${total}</span>
        </div>
        <button className={`mobile-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>
           {isReady ? "ADD TO CART" : "INCOMPLETE"}
        </button>
      </div>

      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareableURL={shareableURL}
        currentConfig={getCurrentConfig()}
        savedConfigs={savedConfigs}
        onSaveConfig={saveNamedConfig}
        loadConfig={loadConfig}
        onDeleteConfig={deleteNamedConfig}
        onCopyLink={copyToClipboard}
        copySuccess={copySuccess}
        gunName={selectedGun?.name}
        colorName={selectedColor?.name}
      />
    </div>
  );
}

export default MasterBlasterBuilder;